import { registerIndicator, type KLineData, type Indicator } from 'klinecharts'
import { type IndicatorTemplateParam, registerOnce } from './registrationUtils'
import { sessionDayKey, DEFAULT_SESSION_TZ } from './sessionDay'

/**
 * Standard (Floor / Classic) Pivot Points.
 *
 * For each trading period, the pivot and its support/resistance levels are
 * derived from the PREVIOUS period's high, low and close:
 *
 *   P  = (H + L + C) / 3
 *   R1 = 2P − L        S1 = 2P − H
 *   R2 = P + (H − L)   S2 = P − (H − L)
 *   R3 = H + 2(P − L)  S3 = L − 2(H − P)
 *
 * "Period" is one TRADING day in the instrument's EXCHANGE-SESSION timezone —
 * NOT UTC, and NOT the user's display timezone. Pivots are an objective, shared
 * level: every viewer of the same symbol must see identical values, so the day
 * boundary is a property of the exchange's session, not of how the user chooses
 * to label times on the axis. For US equities that session is Eastern Time
 * (midnight UTC falls mid-after-hours, so UTC would split a session wrong; a
 * Pacific *display* must not shift the boundary either — it only relabels the
 * axis via chart.setTimezone).
 *
 * This generalises across timeframes: intraday bars are aggregated into their
 * trading day (levels drawn as flat per-day segments, broken at the day
 * boundary — see drawPivotSegments); on daily-or-coarser
 * charts each bar is already its own session, so the levels come from the
 * PREVIOUS BAR (its close included). Constant across a period, stepping at the
 * boundary. Rendered on the main (price) pane.
 *
 * `sessionTimezone` is passed via the indicator's `extendData` for future
 * per-exchange support (crypto → UTC, forex → 5pm ET, etc.); it defaults to the
 * US-equities session and is deliberately NOT the display timezone.
 */

const PIVOT_INDICATOR_NAME = 'PIVOT'
// Session-day bucketing (ET by default) is shared with the other session-scoped
// indicators via sessionDay.ts — see `sessionDayKey` / `DEFAULT_SESSION_TZ`.

// Resistance red, support green, pivot amber — the style editor can override.
const PIVOT_COLOR = '#FFB74D'
const RESISTANCE_COLOR = '#F23645'
const SUPPORT_COLOR = '#089981'

export interface PivotLevels {
  p?: number
  r1?: number
  r2?: number
  r3?: number
  s1?: number
  s2?: number
  s3?: number
}

interface OhlcBar {
  timestamp: number
  high: number
  low: number
  close: number
}

/** Floor-pivot levels from a completed period's high / low / close. */
function pivotsFrom(high: number, low: number, close: number): PivotLevels {
  const p = (high + low + close) / 3
  const range = high - low
  return {
    p,
    r1: 2 * p - low,
    s1: 2 * p - high,
    r2: p + range,
    s2: p - range,
    r3: high + 2 * (p - low),
    s3: low - 2 * (high - p),
  }
}

/**
 * Per-bar pivot levels. Bars in the same session day (bucketed by
 * `sessionTimezone`) share one level set derived from the previous session day;
 * the first day is empty (nothing prior to derive from).
 * Pure + unit-tested; `registerPivotPointsIndicator` wires it into klinecharts.
 */
export function computePivotPoints(
  dataList: OhlcBar[],
  sessionTimezone: string = DEFAULT_SESSION_TZ,
): PivotLevels[] {
  let curDayKey: string | null = null
  let dayHigh = -Infinity
  let dayLow = Infinity
  let dayClose = 0
  // Levels for the CURRENT day, from the last completed day. A fresh object per
  // day, so bars pushed for earlier days keep pointing at their own set.
  let levels: PivotLevels = {}

  return dataList.map((bar) => {
    const dayKey = sessionDayKey(bar.timestamp, sessionTimezone)
    if (curDayKey === null) {
      curDayKey = dayKey
      dayHigh = bar.high
      dayLow = bar.low
      dayClose = bar.close
    } else if (dayKey !== curDayKey) {
      // The day we were accumulating just completed — its H/L/C now drive the
      // new day's levels.
      levels = pivotsFrom(dayHigh, dayLow, dayClose)
      curDayKey = dayKey
      dayHigh = bar.high
      dayLow = bar.low
      dayClose = bar.close
    } else {
      dayHigh = Math.max(dayHigh, bar.high)
      dayLow = Math.min(dayLow, bar.low)
      dayClose = bar.close // last close of the day
    }
    return levels
  })
}

// Figure order is load-bearing: buildIndicatorStyles emits `styles.lines` in
// catalog order, and drawPivotSegments indexes into it by this same position.
const LEVEL_FIGURES: Array<{ key: keyof PivotLevels; title: string; color: string }> = [
  { key: 'p', title: 'P: ', color: PIVOT_COLOR },
  { key: 'r1', title: 'R1: ', color: RESISTANCE_COLOR },
  { key: 'r2', title: 'R2: ', color: RESISTANCE_COLOR },
  { key: 'r3', title: 'R3: ', color: RESISTANCE_COLOR },
  { key: 's1', title: 'S1: ', color: SUPPORT_COLOR },
  { key: 's2', title: 'S2: ', color: SUPPORT_COLOR },
  { key: 's3', title: 'S3: ', color: SUPPORT_COLOR },
]

export interface IndicatorSegmentLineStyle {
  color?: string
  size?: number
  style?: string
  dashedValue?: number[]
  visible?: boolean
}

export interface IndicatorSegmentFigure<TKey extends string = string> {
  key: TKey
  title?: string
  color: string
}

export interface IndicatorSegmentParams<TRecord extends object> {
  ctx: Pick<
    CanvasRenderingContext2D,
    'save' | 'restore' | 'beginPath' | 'moveTo' | 'lineTo' | 'stroke' | 'setLineDash'
  > & { strokeStyle: string | CanvasGradient | CanvasPattern; lineWidth: number }
  indicator: {
    result: readonly (TRecord | undefined)[]
    styles?: {
      lines?: readonly IndicatorSegmentLineStyle[]
    } | null
  }
  visibleRange: { from: number; to: number }
  barSpace: { halfBar: number }
  xAxis: { convertToPixel: (dataIndex: number) => number }
  yAxis: { convertToPixel: (value: number) => number }
}

export type PivotDrawParams = IndicatorSegmentParams<PivotLevels>

/**
 * Custom renderer: each pivot level is a flat horizontal segment per session
 * day, with a hard break at the day boundary. The default `line` figure render
 * would connect consecutive per-bar values into one polyline, drawing a
 * diagonal from yesterday's level to today's — pivots are discrete daily
 * levels, so that transition stroke is noise, not data.
 *
 * Day runs are detected by reference identity: computePivotPoints returns ONE
 * shared levels object for every bar of a session day.
 */
export function drawIndicatorSegments<
  TKey extends string,
  TRecord extends { [K in TKey]?: number },
>(
  params: IndicatorSegmentParams<TRecord>,
  figures: readonly IndicatorSegmentFigure<TKey>[],
): boolean {
  const { ctx, indicator, visibleRange, barSpace, xAxis, yAxis } = params
  const result = indicator.result
  const lines = indicator.styles?.lines
  const from = Math.max(0, visibleRange.from)
  const to = Math.min(result.length, visibleRange.to)
  if (to <= from) return true

  ctx.save()
  let runStart = from
  for (let i = from + 1; i <= to; i++) {
    if (i < to && result[i] === result[runStart]) continue
    const levels = result[runStart]
    if (levels) {
      // Segment spans the run's bars edge-to-edge (half a bar past each
      // center). Runs clipped by the viewport still cover the visible part.
      const x0 = xAxis.convertToPixel(runStart) - barSpace.halfBar
      const x1 = xAxis.convertToPixel(i - 1) + barSpace.halfBar
      figures.forEach((fig, figIdx) => {
        const value = levels[fig.key]
        if (value == null) return
        const style = lines?.[figIdx]
        // Per-level hide (Style tab toggle) — skip disabled levels entirely.
        if (style?.visible === false) return
        const y = yAxis.convertToPixel(value)
        ctx.strokeStyle = style?.color ?? fig.color
        ctx.lineWidth = style?.size ?? 1
        ctx.setLineDash(style?.style === 'dashed' ? (style.dashedValue ?? [2, 2]) : [])
        ctx.beginPath()
        ctx.moveTo(x0, y)
        ctx.lineTo(x1, y)
        ctx.stroke()
      })
    }
    runStart = i
  }
  ctx.restore()

  // Returning true skips klinecharts' default figure rendering.
  return true
}

/** Draw pivot levels with the generic horizontal-segment renderer. */
export function drawPivotSegments(params: PivotDrawParams): boolean {
  return drawIndicatorSegments(params, LEVEL_FIGURES)
}

/** Register the `PIVOT` main-pane indicator with klinecharts (once). */
export const registerPivotPointsIndicator = registerOnce(() => {
  registerIndicator({
    name: PIVOT_INDICATOR_NAME,
    shortName: 'Pivots',
    series: 'price' as never,
    calcParams: [],
    precision: 2,
    // Figures still declare the levels (legend readouts + y-axis range);
    // `draw` below replaces only how they are stroked.
    figures: LEVEL_FIGURES.map(({ key, title, color }) => ({
      key,
      title,
      type: 'line',
      styles: () => ({ color }),
    })),
    calc: (dataList: KLineData[], indicator: Indicator) => {
      const tz = (indicator.extendData as { timezone?: string } | undefined)?.timezone
      return computePivotPoints(dataList, tz || DEFAULT_SESSION_TZ)
    },
    draw: drawPivotSegments as never,
  } as IndicatorTemplateParam)
})

export { PIVOT_INDICATOR_NAME }
